// The credit line a layout can carry in its bottom corner.
//
// A Custom HTML/CSS block's sources have always gone into the deck's reference
// list, but the note that belongs *on* the slide was the author's own job:
// copy the in-text citation out of the editor and weave it into the markup by
// hand, in a layout they may not have written. Ticking a box is a better deal,
// and it is the only way the note survives a layout being regenerated by an
// assistant.
//
// It is emitted as markup and CSS rather than drawn by the renderer, because
// the block reaches the screen three ways — the React renderer, the HTML
// export, and the SVG the PDF and PowerPoint are rasterized from — and all
// three go through prepareCustomHtmlBlock. Adding it there means the note
// cannot be in the app but missing from the handout.
import { formatInText, mapCitationToSourceMeta } from '../lib/apaSlideFormatter';
import { hasApaCitationContent } from './apaCitationUtils';

/** The class the overlay carries, so an author can restyle it themselves. */
export const CITATION_OVERLAY_CLASS = 'lp-cite-overlay';

/**
 * The grab handle the editor puts in front of the text.
 *
 * Editor-only: it is drawn by the React renderer, not by citationOverlayHtml,
 * so nothing about the exported page or the rasterized slide changes. It
 * exists because a credit line that can be dragged looks exactly like a credit
 * line that cannot — a cursor change alone is a thing you find by accident.
 */
export const CITATION_OVERLAY_GRIP_CLASS = 'lp-cite-overlay__grip';

const clampPercent = (value) => Math.min(100, Math.max(0, value));

/**
 * Where the author dragged the note to, as percentages of the block's own box,
 * or `null` for "leave it in the corner".
 *
 * Percentages rather than pixels because the block is one width in the editor,
 * another at presentation scale and another again in the rasterized slide, and
 * a note pinned in inches would land somewhere different in each.
 *
 * Normalised hard, and by this function alone, because the value arrives from
 * a deck file that anyone may have edited and its next stop is a CSS string.
 * Anything that is not two real numbers is not a position.
 */
export const normalizeCitationOverlayPos = (pos) => {
  if (!pos || typeof pos !== 'object') return null;
  const x = Number(pos.x);
  const y = Number(pos.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return {
    x: Math.round(clampPercent(x) * 100) / 100,
    y: Math.round(clampPercent(y) * 100) / 100
  };
};

/** Is this block asking for the corner note? */
export const wantsCitationOverlay = (block = {}) => (
  block.citationOverlay === true && block.includeCitation !== false
);

const escapeHtml = (value) => String(value == null ? '' : value)
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

/**
 * The line itself: every filled-in citation as its in-text note.
 *
 * The in-text form rather than the full reference, because this is a credit in
 * the corner of a slide and the full entry is what the reference list at the
 * back is for. Several sources are joined into one line — a stack of them in a
 * corner is a footnote, and a slide is not a page.
 */
export const citationOverlayText = (block = {}) => {
  const citations = Array.isArray(block.citations) ? block.citations : [];
  return citations
    .filter((citation) => hasApaCitationContent(citation))
    .map((citation) => formatInText(mapCitationToSourceMeta(citation)))
    .map((note) => (note || '').trim())
    .filter(Boolean)
    .join('; ');
};

/** The overlay's markup, or '' when there is nothing to show. */
export const citationOverlayHtml = (block = {}) => {
  if (!wantsCitationOverlay(block)) return '';
  const text = citationOverlayText(block);
  if (!text) return '';
  return `<div class="${CITATION_OVERLAY_CLASS}">${escapeHtml(text)}</div>`;
};

/**
 * The overlay's own styling, already scoped to the block.
 *
 * Sized in `cqw` like everything else in a layout, so it is the same size in
 * the editor, the exported page and the rasterized slide. 1.25cqw is 12pt at
 * the deck's slide width (9.6pt per cqw) — the floor the brief sets for
 * readable type, which is right for a credit line and too small for anything
 * else.
 *
 * The backing is what makes it legible over a photograph; it is deliberately
 * light rather than a solid chip, and an author who wants it different can say
 * so in their own CSS — the class is stable and their rules land in the same
 * scope, after these.
 */
export const citationOverlayCss = (scopeSelector, pos) => {
  const scope = scopeSelector || '';
  const root = scope ? `${scope}` : `.slide-custom-html`;
  const placed = normalizeCitationOverlayPos(pos);
  // Dragged, or left where it starts. The corner is the default because it is
  // where a credit line belongs; the override exists for the layouts where
  // something of the author's is already there.
  //
  // The resting inset clears the slide number, which sits in the same corner
  // whenever the deck is numbered — 2.2cqw in from the slide's edge and about
  // 1.6cqw wide at three digits. Landing on top of it is what the author sees
  // first, and a note they have to drag before the slide reads properly is a
  // note that starts in the wrong place.
  const placement = placed
    ? `  left: ${placed.x}%;
  top: ${placed.y}%;
  right: auto;
  bottom: auto;`
    : `  right: 6cqw;
  bottom: 1.4cqw;`;
  return `${root} {
  position: relative;
}
${root} .${CITATION_OVERLAY_CLASS} {
  position: absolute;
${placement}
  z-index: 20;
  max-width: 62cqw;
  margin: 0;
  padding: 0.35cqw 0.7cqw;
  border-radius: 0.5cqw;
  background: rgba(255, 255, 255, 0.72);
  color: #1f2937;
  font-size: 1.25cqw;
  line-height: 1.35;
  text-align: ${placed ? 'left' : 'right'};
  text-wrap: balance;
  /* Inert everywhere by default — a credit line must never eat a click on a
     link underneath it. The editor adds is-movable to the one card it is
     letting the author drag. */
  pointer-events: none;
}
${root} .${CITATION_OVERLAY_CLASS}.is-movable {
  /* Above the slide designer's capture layer (z-index 5), which covers every
     block on a canvas slide and turns any drag into "move this block". Without
     this the note could be seen but never picked up. */
  z-index: 30;
  pointer-events: auto;
  cursor: move;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5cqw;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 0 0 1px rgba(124, 58, 237, 0.75), 0 1px 4px rgba(15, 23, 42, 0.18);
}
${root} .${CITATION_OVERLAY_CLASS}.is-movable:hover {
  box-shadow: 0 0 0 2px rgba(124, 58, 237, 0.95), 0 2px 6px rgba(15, 23, 42, 0.22);
}
${root} .${CITATION_OVERLAY_CLASS} .${CITATION_OVERLAY_GRIP_CLASS} {
  /* Two columns of dots, drawn rather than set in a glyph so it cannot land on
     a machine without the font for it. */
  flex: 0 0 auto;
  width: 0.9cqw;
  height: 1.4cqw;
  background-image: radial-gradient(currentColor 42%, transparent 45%);
  background-size: 0.45cqw 0.47cqw;
  opacity: 0.5;
}
${root} .${CITATION_OVERLAY_CLASS}.is-movable:hover .${CITATION_OVERLAY_GRIP_CLASS} {
  opacity: 0.85;
}`;
};
